/**
 * @zendir/ui - GlobalStatusBar Component (Pure React)
 *
 * Primary header component for space applications following Astro UX patterns.
 *
 * Features:
 * - Theme-integrated via useTheme()
 * - App identity (name, domain, version)
 * - Clock slot
 * - Monitoring icons slot
 * - Username display
 * - Responsive design
 */

import React, { memo, ReactNode } from "react";
import { useTheme } from "../theme";
import { classNames, safeAccentText } from "../utils";

export interface GlobalStatusBarProps {
  /** Application name */
  appName?: string;
  /** Application domain (organization/mission) */
  appDomain?: string;
  /** Application version */
  appVersion?: string;
  /** Username to display */
  username?: string;
  /** Left slot content (app switcher, etc.) */
  leftSlot?: ReactNode;
  /** Center slot content (typically clock) */
  centerSlot?: ReactNode;
  /** Right slot content (monitoring icons, controls) */
  rightSlot?: ReactNode;
  /** Additional children */
  children?: ReactNode;
  /** Custom className */
  className?: string;
}

/**
 * GlobalStatusBar - Pure React global status bar
 *
 * Primary header component for space applications. Includes slots for:
 * - App switcher (left)
 * - Global clock (center)
 * - Monitoring icons (right)
 *
 * @example
 * ```tsx
 * <GlobalStatusBar
 *   appName="Mission Control"
 *   appDomain="Zendir"
 *   username="Demo User"
 *   centerSlot={<MissionClock />}
 *   rightSlot={<MonitoringIcons />}
 * />
 * ```
 */
export const GlobalStatusBar = memo(function GlobalStatusBar({
  appName = "Zendir",
  appDomain,
  appVersion,
  username,
  leftSlot,
  centerSlot,
  rightSlot,
  children,
  className = "",
}: GlobalStatusBarProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  return (
    <header
      className={classNames("zendir-global-status-bar", className)}
      role="banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 56,
        padding: `0 ${tokens.spacing.lg}`,
        backgroundColor: isTransparentTheme
          ? "transparent"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)" as const,
        }),
        borderBottom: `1px solid ${tokens.colors.border.muted}`,
        color: tokens.colors.text.primary,
        fontFamily: tokens.typography.fontFamily.primary,
      }}
    >
      {/* Left section: App identity */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.lg,
          flex: "1 1 0",
        }}
      >
        {leftSlot}

        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: tokens.spacing.sm,
          }}
        >
          {appDomain && (
            <span
              style={{
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.normal,
                color: tokens.colors.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: tokens.typography.letterSpacing.wide,
              }}
            >
              {appDomain}
            </span>
          )}
          <span
            style={{
              fontSize: tokens.typography.fontSize.xl,
              fontWeight: tokens.typography.fontWeight.semibold,
              letterSpacing: tokens.typography.letterSpacing.tight,
              color: tokens.colors.text.primary,
            }}
          >
            {appName}
          </span>
          {appVersion && (
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.normal,
                color: tokens.colors.text.tertiary,
              }}
            >
              v{appVersion}
            </span>
          )}
        </div>
      </div>

      {/* Center section: Clock */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        {centerSlot}
        {children}
      </div>

      {/* Right section: Monitoring + User */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: tokens.spacing.lg,
          flex: "1 1 0",
        }}
      >
        {rightSlot}

        {username && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              paddingLeft: tokens.spacing.lg,
              borderLeft: `1px solid ${tokens.colors.border.muted}`,
            }}
          >
            {/* User avatar */}
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: tokens.borderRadius.full,
                backgroundColor: `${tokens.colors.accent.primary}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.semibold,
                color: safeAccentText(tokens.colors.accent.primary),
              }}
            >
              {username.charAt(0).toUpperCase()}
            </div>
            <span
              style={{
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                color: tokens.colors.text.primary,
              }}
            >
              {username}
            </span>
          </div>
        )}
      </div>
    </header>
  );
});
